import React, { useState } from 'react';
import SectionHeader from './SectionHeader';

const SKILL_CATEGORIES = [
  {
    name: 'Frontend',
    icon: '🎨',
    gradient: 'linear-gradient(135deg, #8b5cf6, #3b82f6)',
    skills: [
      { name: 'React.js', level: 95 },
      { name: 'Next.js', level: 90 },
      { name: 'TypeScript', level: 85 },
      { name: 'JavaScript (ES6+)', level: 95 },
      { name: 'HTML5', level: 95 },
      { name: 'CSS3', level: 90 },
      { name: 'React Context API', level: 88 },
    ],
  },
  {
    name: 'Backend & Services',
    icon: '⚙️',
    gradient: 'linear-gradient(135deg, #06b6d4, #10b981)',
    skills: [
      { name: 'REST API', level: 88 },
      { name: 'Firebase', level: 82 },
      { name: 'Flask', level: 72 },
      { name: 'Python', level: 78 },
    ],
  },
  {
    name: 'Tools & Libraries',
    icon: '🛠️',
    gradient: 'linear-gradient(135deg, #f59e0b, #ef4444)',
    skills: [
      { name: 'Plotly.js', level: 85 },
      { name: 'Git & GitHub', level: 90 },
      { name: 'Server-Side Pagination', level: 85 },
      { name: 'Lazy Loading', level: 88 },
    ],
  },
];

export default function Skills() {
  const [activeCategory, setActiveCategory] = useState(0);

  return (
    <section id="skills" style={{ padding: '7rem 0', position: 'relative' }}>
      {/* Background accent */}
      <div
        style={{
          position: 'absolute',
          top: '30%',
          right: '-200px',
          width: '400px',
          height: '400px',
          borderRadius: '50%',
          background: 'var(--accent-cyan)',
          filter: 'blur(120px)',
          opacity: 0.05,
          pointerEvents: 'none',
        }}
      />

      <div className="section-container">
        <SectionHeader
          label="Skills"
          title="Technical Arsenal"
          subtitle="Technologies and tools I use to bring ideas to life."
        />

        {/* Category Tabs */}
        <div
          style={{
            display: 'flex',
            justifyContent: 'center',
            gap: '0.5rem',
            marginTop: '2.5rem',
            marginBottom: '2.5rem',
            flexWrap: 'wrap',
          }}
        >
          {SKILL_CATEGORIES.map((cat, i) => (
            <button
              key={cat.name}
              onClick={() => setActiveCategory(i)}
              style={{
                padding: '0.625rem 1.25rem',
                borderRadius: '10px',
                fontSize: '0.9rem',
                fontWeight: 600,
                cursor: 'pointer',
                border: 'none',
                fontFamily: 'var(--font-body)',
                transition: 'all 0.3s ease',
                background: activeCategory === i ? 'rgba(139, 92, 246, 0.15)' : 'rgba(255,255,255,0.04)',
                color: activeCategory === i ? 'var(--accent-violet-light)' : 'var(--text-secondary)',
                borderWidth: '1px',
                borderStyle: 'solid',
                borderColor: activeCategory === i ? 'rgba(139, 92, 246, 0.3)' : 'rgba(255,255,255,0.06)',
              }}
              id={`skills-tab-${i}`}
            >
              <span style={{ marginRight: '0.4rem' }}>{cat.icon}</span>
              {cat.name}
            </button>
          ))}
        </div>

        {/* Skills Grid */}
        <div
          className="glass-card"
          style={{ padding: '2rem', maxWidth: '700px', margin: '0 auto' }}
        >
          <div style={{ display: 'flex', flexDirection: 'column', gap: '1.25rem' }}>
            {SKILL_CATEGORIES[activeCategory].skills.map((skill) => (
              <div key={skill.name}>
                <div
                  style={{
                    display: 'flex',
                    justifyContent: 'space-between',
                    marginBottom: '0.5rem',
                    alignItems: 'center',
                  }}
                >
                  <span style={{ fontSize: '0.9rem', fontWeight: 600, color: 'var(--text-primary)' }}>
                    {skill.name}
                  </span>
                  <span
                    style={{
                      fontSize: '0.75rem',
                      fontFamily: 'var(--font-mono)',
                      color: 'var(--text-muted)',
                    }}
                  >
                    {skill.level}%
                  </span>
                </div>
                <div
                  style={{
                    height: '6px',
                    borderRadius: '3px',
                    background: 'rgba(255,255,255,0.06)',
                    overflow: 'hidden',
                  }}
                >
                  <div
                    style={{
                      height: '100%',
                      borderRadius: '3px',
                      background: SKILL_CATEGORIES[activeCategory].gradient,
                      width: `${skill.level}%`,
                      transition: 'width 0.8s ease',
                    }}
                  />
                </div>
              </div>
            ))}
          </div>
        </div>

        {/* Tech Stack Tags */}
        <div
          style={{
            display: 'flex',
            flexWrap: 'wrap',
            justifyContent: 'center',
            gap: '0.6rem',
            marginTop: '3rem',
          }}
        >
          {['React.js', 'Next.js', 'TypeScript', 'JavaScript', 'HTML5', 'CSS3', 'Python', 'Firebase', 'Supabase', 'REST API', 'Flask', 'Plotly.js', 'Git', 'React Context API'].map(
            (tech) => (
              <span
                key={tech}
                style={{
                  padding: '0.4rem 1rem',
                  borderRadius: '999px',
                  fontSize: '0.8rem',
                  fontWeight: 500,
                  fontFamily: 'var(--font-mono)',
                  background: 'rgba(255,255,255,0.04)',
                  color: 'var(--text-secondary)',
                  border: '1px solid rgba(255,255,255,0.06)',
                  transition: 'all 0.3s ease',
                  cursor: 'default',
                }}
                onMouseEnter={(e) => {
                  e.target.style.background = 'rgba(139, 92, 246, 0.1)';
                  e.target.style.color = 'var(--accent-violet-light)';
                  e.target.style.borderColor = 'rgba(139, 92, 246, 0.25)';
                  e.target.style.transform = 'translateY(-2px)';
                }}
                onMouseLeave={(e) => {
                  e.target.style.background = 'rgba(255,255,255,0.04)';
                  e.target.style.color = 'var(--text-secondary)';
                  e.target.style.borderColor = 'rgba(255,255,255,0.06)';
                  e.target.style.transform = 'translateY(0)';
                }}
              >
                {tech}
              </span>
            ),
          )}
        </div>
      </div>
    </section>
  );
}
